import { CurrencyPipe, DecimalPipe } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	HostBinding,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	Optional,
	Output,
	TemplateRef,
	ViewChild,
} from '@angular/core';
import { Router, Params } from '@angular/router';
import {
	ApiService,
	AppSettingsService,
	BreakPointService,
	CallToActionResponse,
	DatalayerService,
	FeatureService,
	GenericModal,
	getProductType,
	kebabCase,
	ListType,
	LocalStorageService,
	LoggingService,
	ModalEvent,
	ModalOverlayRef,
	ModalOverlayService,
	ModalRoutingService,
	NotificationType,
	PopupService,
	ProductDatalayerEvent,
	ProductType,
	REQ_RECEIVE_TIME,
	SearchTarget,
	getDiscountDiffValue,
	PromotionalCarouselResponseModel,
	sentenceCase,
} from '@woolworthsnz/styleguide';
import {
	BoostOfferProductTag,
	ContextResponse,
	ProductResponse,
	ProductTag,
	QuantityResponse,
	TrolleyItemResponse,
} from '@woolworthsnz/trader-api';
import { TealiumUtagService, productSelectConstants } from '@woolworthsnz/analytics';
import { finalize, takeUntil } from 'rxjs/operators';
import { extractQLCodeFromCtaName, getFormattedAvailabilityStatus, shouldShowTagAsStrap } from '../../helpers/';
import { getOfferText } from '../../helpers/targeted-offer.helper';
import { LayoutType } from '../../models/layout-type.model';
import { ProductTagType } from '../../models/product-tag.model';
import { CitrusAdService } from '../../services/citrus-ad.service';
import { ProductService } from '../../services/product.service';
import { ProductState } from '../../services/product.state';
import { ProductSource } from '../../models/tealium-events';
import { Subject } from 'rxjs';

const tagTypeEnum = ProductTag.TagTypeEnum;

/**
 * This class is the base class for product-stamp-list and product-stamp-grid
 * Whilst we normally avoid base class inheritance, it makes sense here
 * as they are functionally identical, yet laid out differently
 */
@Component({
	selector: 'product-stamp',
	template: '',
	providers: [CurrencyPipe, DecimalPipe, ApiService, ProductService],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
})
export class StampComponent implements OnInit, OnDestroy {
	@HostBinding('attr.role') role = 'article';
	@HostBinding('attr.data-targeted-offer')
	get isTargetedOffer(): boolean {
		return this.product?.productTag?.tagType === tagTypeEnum.IsTargetedOffer;
	}

	@Input() public content?: PromotionalCarouselResponseModel;
	@Input() canRemoveFromList = false;
	@Input() endpoints: {
		addToTrolley: string;
		shopperNotes: string;
	};
	@Input() listId: string;
	@Input() listName: string;
	@Input() listType: ListType;
	@Input() position = 0;
	@Input() cppItemCount?: number;

	@Input() set product(p: ProductResponse) {
		this.updateProductService(p);
	}

	@Input() set showBoughtBefore(value: boolean) {
		this._showBoughtBefore = value;
	}

	get product(): ProductResponse {
		return this.productService.state.product as ProductResponse;
	}

	@Input() isShopper = false;
	@Input() isLoggedIn = false;
	@Input() uniqueid: number;

	@Input() isGridCarousel = false;
	@Input() sourceCarousel: string;
	@Input() pageSource: string;
	@Input() carouselindex: number;
	@Input() isExistingSkuWhenChangingOrder: false;
	@Input() target: string;
	@Input() department?: string;
	@Input() isBoostOfferActivatedFromState: boolean;
	@Input() hideUnavailableProductPrice = false;
	@Input()
	set promotion(promoInfo: PromotionalCarouselResponseModel) {
		this.productService.promotion = promoInfo;
	}

	@Output() removeProductEmitter: EventEmitter<string> = new EventEmitter();
	@Output() visiblityChange = new EventEmitter<boolean>();
	@Output() updateProductEmitter = new EventEmitter<Map<string, TrolleyItemResponse>>();
	@Output() allowSubstitutesProductEmitter: EventEmitter<string> = new EventEmitter();
	@Output() disallowSubstitutesProductEmitter: EventEmitter<string> = new EventEmitter();

	@ViewChild('stampLink') stampLink: ElementRef;
	@ViewChild('shopperNotes') shopperNotesModal: TemplateRef<any>;
	@ViewChild('removeFromFavourites') removeFromFavouritesModal: TemplateRef<any>;

	addToTrolleyFailed = false;
	currentAction: ModalEvent;
	errored = false;
	errorTitle = 'There was a problem adding to the trolley';
	errorText = '';
	itemInTrolley = 0;
	removeFromListLabel = 'Remove from saved list';
	shopperNoteLabelAdd = 'Add shopper note';
	shopperNote: string;
	shouldCenterModalContent = true;
	visible = true;
	layout = LayoutType;
	isIndicateChangedPriceEnabled = false;
	isOnlineSamplePastOrdersEnabled: boolean;
	haystackEDRChangesEnabled = false;
	private destroyed$: Subject<boolean> = new Subject();
	// eslint-disable-next-line @typescript-eslint/naming-convention
	private _showNotification = false;
	private shopperNotesModalRef?: ModalOverlayRef;
	private _showBoughtBefore = false;
	private enableCartologyGA4EventTracking = false;

	constructor(
		protected modalOverlayService: ModalOverlayService,
		public productService: ProductService,
		protected cdr: ChangeDetectorRef,
		protected appSettingsService: AppSettingsService,
		protected datalayerService: DatalayerService,
		protected citrusAdService: CitrusAdService,
		protected modalRoutingService: ModalRoutingService,
		protected featureService: FeatureService,
		protected router: Router,
		protected popupService: PopupService,
		protected localStorageService: LocalStorageService,
		protected breakPointService: BreakPointService,
		protected loggingService: LoggingService,
		@Optional() @Inject(REQ_RECEIVE_TIME) protected originalTime = 0,
		protected tealiumUtagService: TealiumUtagService
	) {}
	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	get productDepartmentName(): string | undefined {
		if (this.department) {
			return this.department;
		}
		if (!this.product?.departments) {
			return;
		}
		return this.product?.departments[0]?.name;
	}

	get cupPrice(): string | false {
		return this.productService.cupPrice;
	}

	get noMemberCupPrice(): string {
		return this.productService.noMemberCupPrice;
	}

	get multiCupPrice(): string {
		return this.productService.multiCupPrice;
	}

	get shouldShowMultiBuyCupPrice(): boolean {
		return this.productService.shouldShowMultiBuyCupPrice;
	}

	get shopperNoteLabel(): string {
		return this.hasShopperNotes
			? `Change shopper notes related to ${this.product?.name}`
			: `Add shopper notes related to ${this.product?.name}`;
	}

	get inTrolleyText(): string {
		let itemInTrolley = this.itemInTrolley || 0;
		const showKgSuffix = this.isKgItem && this.pricingUnit !== ProductResponse.UnitEnum.Each;

		if (this.isKgItem && this.pricingUnit === ProductResponse.UnitEnum.Each) {
			itemInTrolley = this.productService.getEachQuantityForGivenWeight(
				itemInTrolley,
				this.product?.averageWeightPerUnit
			);
		}

		return `${itemInTrolley}${showKgSuffix ? 'kg' : ''} in trolley`;
	}

	get isKgItem(): boolean {
		return this.unit === ProductResponse.UnitEnum.Kg;
	}

	get isFromFavouritesList(): boolean {
		return this.listType === ListType.Favourites;
	}

	get showBoughtBefore(): boolean {
		return this._showBoughtBefore;
	}

	get isOutOfStockEnabled(): boolean {
		return (
			this.target === SearchTarget.Search ||
			this.target === SearchTarget.Browse ||
			[ListType.PastOrders, ListType.SavedList, ListType.Favourites].includes(this.listType)
		);
	}

	get modalDataForRemoveFromList(): GenericModal {
		return {
			buttonText: 'Remove',
			title: 'Are you sure you want to remove this item?',
			iconFill: 'warning',
			icon: this.removeIcon,
			hasActionButton: true,
			closeAction: () => this.modalOverlayService.close(),
		};
	}

	get itemTotal(): string | undefined {
		return this.productService.total;
	}

	get savingsTotal(): string | undefined {
		return this.productService.discount === '$0.00' ? '' : this.productService.discount;
	}

	get nameInKebabCase(): string {
		return kebabCase(this.product?.name || '');
	}

	get price(): number | undefined {
		return this.productService.salePrice;
	}

	get productIsAvailable(): boolean {
		return this.productService.productIsAvailable;
	}

	get productIsInStock(): boolean {
		return this.productService.productIsInStock;
	}

	get productAvailabilityStatus(): string | undefined {
		return this.productService.productAvailabilityStatus;
	}

	get hasShopperNotes(): boolean {
		return this.productService.hasShopperNotes;
	}

	get shopperNoteFromState(): string {
		return this.productService.shopperNote?.replace(/\\n/g, ' ');
	}

	get quantityResponse(): QuantityResponse | undefined {
		return this.productService.quantityResponse;
	}

	get removeIcon(): string {
		return this.listType === ListType.SavedList ? 'trash' : 'remove-my-favourites';
	}

	get showShopperNotesModalContent(): boolean {
		return this.currentAction === ModalEvent.addShopperNote && !this.addToTrolleyFailed;
	}

	get showRemoveModalContent(): boolean {
		return this.currentAction === ModalEvent.deleteFromList && !this.errored;
	}

	get sku(): string | undefined {
		return this.productService.sku;
	}

	get shopperNotesIcon(): string {
		return `notes-${this.hasShopperNotes ? 'filled' : 'default'}`;
	}

	get showNotification(): boolean {
		return this._showNotification;
	}

	set showNotification(show: boolean) {
		if (!this._showNotification && show) {
			this._showNotification = show;

			setTimeout(
				() => {
					this._showNotification = false;
					this.cdr.markForCheck();
				},
				parseInt(this.appSettingsService.getTransition('longer'), 10)
			);
		}
	}

	get supportsBothEachAndKgPricing(): boolean | undefined {
		return this.productService.supportsBothEachAndKgPricing;
	}

	get unit(): TrolleyItemResponse.UnitEnum | undefined {
		return this.productService.unit;
	}

	// Confused by this? See the productService for more info
	get pricingUnit(): TrolleyItemResponse.UnitEnum | undefined {
		return this.productService.pricingUnit;
	}

	get offerText(): string | undefined {
		return getOfferText(this.product.productTag?.targetedOffer);
	}

	get isEachKgItemAdded(): boolean | undefined {
		return this.productService.isEachKgItemAdded;
	}

	get showBoughtBeforeLabel(): boolean {
		return this.showBoughtBefore && !!this.productService.boughtBefore;
	}

	get shouldTagShowAsStrap(): boolean {
		return shouldShowTagAsStrap(this.product?.productTag?.tagType);
	}

	get hideTagsForItemWithChangedPrice(): boolean {
		return (
			this.isIndicateChangedPriceEnabled &&
			(this.product?.price?.isUsingOrderedPrice ?? false) &&
			this.product?.price?.currentPricingMatchesOrderedPricing !== null &&
			typeof this.product?.price?.currentPricingMatchesOrderedPricing !== typeof undefined &&
			!this.product?.price?.currentPricingMatchesOrderedPricing
		);
	}

	get tagType(): ProductTagType {
		return tagTypeEnum;
	}

	get ariaLabelIds(): string {
		// These are read out in order
		const labelSuffixes = [
			'title',
			'tag',
			'price',
			'save',
			'promoInfo',
			'unitPrice',
			'price-single-unit-average',
			'unavailable',
		];
		return labelSuffixes.map((labelSuffix) => `product-${this.uniqueid}-${labelSuffix}`).join(' ');
	}

	get isSampledProductInPastOrders(): boolean {
		return this.listType === ListType.PastOrders && this.product?.productTag?.tagType === tagTypeEnum.IsSampled;
	}

	get shouldShowIsSampledTag(): boolean {
		return (
			this.isOnlineSamplePastOrdersEnabled &&
			this.listType === ListType.PastOrders &&
			this.product?.productTag?.tagType === tagTypeEnum.IsSampled &&
			!this.productIsAvailable
		);
	}

	get productType(): ProductType {
		return getProductType(this.isGridCarousel, this.product?.adId, this.product?.onlineSampleRealProductMapId);
	}

	get isEachKgMeatProduct(): boolean {
		return !!(this.supportsBothEachAndKgPricing && this.productDepartmentName?.match(/meat/i));
	}

	get productTitle(): string | undefined {
		return this.isEachKgMeatProduct && this.product?.size?.volumeSize
			? `${this.product?.name} ${this.product?.size?.volumeSize}`
			: this.product?.name;
	}

	get isBoostOfferActivatedFromProductInfo(): boolean {
		return (
			this.product?.productTag?.tagType === tagTypeEnum.IsBoostOffer &&
			this.product?.productTag?.boostOffer?.offerStatus === BoostOfferProductTag.OfferStatusEnum.Activated
		);
	}

	get isBoostOfferActivated(): boolean {
		return this.isBoostOfferActivatedFromState || this.isBoostOfferActivatedFromProductInfo;
	}

	get isProductTagClubPrice(): boolean {
		return this.product?.productTag?.tagType === tagTypeEnum.IsClubPrice;
	}

	get shouldShowPrice(): boolean {
		return !this.shouldShowIsSampledTag && !(this.hideUnavailableProductPrice && !this.productIsAvailable);
	}
	get isCPUUIAdjustimentOn(): boolean {
		return this.featureService.isFeatureEnabled(ContextResponse.EnabledFeaturesEnum.EnableCUPUIAdjustments);
	}
	get tagLink(): { path: string; queryParams: string | Params; text: string; linkTarget?: string } | null {
		if (!this.product.productTag) {
			return null;
		}
		const hasMultiBuyTagLink = this.product.productTag.multiBuy && this.product.productTag.multiBuy.link;
		const path = (link: string): string => link.split('?')[0];
		const params = (link: string): Params => this.router.parseUrl(link).queryParams;

		if (hasMultiBuyTagLink) {
			return {
				path: path(this.product.productTag.multiBuy?.link || ''),
				queryParams: params(this.product.productTag.multiBuy?.link || ''),
				text: `${this.product.productTag.multiBuy?.quantity} for ${this.productService.getPriceAsCurrency(this.product.productTag.multiBuy?.value || 0)}`,
				linkTarget: this.product.productTag.additionalTag?.linkTarget,
			};
		}

		return null;
	}
	get volumeSize(): string | undefined {
		if (this.isCPUUIAdjustimentOn && !this.isEachKgMeatProduct) {
			return this.product?.size?.volumeSize;
		} else {
			return undefined;
		}
	}
	get packageType(): string {
		if (this.isCPUUIAdjustimentOn && !this.productService.supportsBothEachAndKgPricing) {
			return (this.product?.size?.packageType && sentenceCase(this.product?.size.packageType)) || '';
		} else {
			return '';
		}
	}

	get shouldShowNoMemberCupPrice(): boolean {
		return this.product?.productTag?.tagType === tagTypeEnum.IsClubPrice;
	}

	ngOnInit(): void {
		if (!this.product) {
			return;
		}

		this.productService.state$
			.pipe(takeUntil(this.destroyed$))
			.subscribe((productState: ProductState): void => this.processProductStateUpdate(productState));

		this.isOnlineSamplePastOrdersEnabled = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.OnlineSamplePastOrders
		);

		this.isIndicateChangedPriceEnabled = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.ChangeOrderIndicateChangedPriceInProductList
		);

		this.haystackEDRChangesEnabled = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.HaystackEDRChanges
		);

		this.featureService
			.isEnabled(ContextResponse.EnabledFeaturesEnum.EnableCartologyGA4EventTracking)
			.pipe(takeUntil(this.destroyed$))
			.subscribe((status) => {
				this.enableCartologyGA4EventTracking = status;
			});
	}

	processProductStateUpdate(productState: ProductState): void {
		const sku = productState.product?.sku;
		if (sku && productState.quantityInTrolley === 0) {
			this.removeProductEmitter.emit(sku);
		}

		if (sku && productState.promotionAffectedItems) {
			this.updateProductEmitter.emit(productState.promotionAffectedItems);
		}
		this.visible = productState.visible;
		this.visiblityChange.emit(this.visible);

		if (
			this.itemInTrolley &&
			productState.completed &&
			parseFloat(productState.quantityInTrolley.toString()) &&
			// This has been disabled due to not working any more -
			// product?.quantity?.value initially didn't get
			// updated which is technically wrong but served
			// the purpose for showing a notification
			this.currentAction === ModalEvent.addToTrolley
		) {
			this.showNotification = true;
		}

		if (productState.completed && !productState.submitted) {
			this.itemInTrolley = productState.quantityInTrolley;
		}

		if (this.itemInTrolley && this.stampLink) {
			this.stampLink.nativeElement.focus();
		}
	}

	onClick(position: number): void {
		const { name, price, sku, brand, departments } = this.product;
		const availabilityStatus = this.productIsAvailable
			? getFormattedAvailabilityStatus(this.productAvailabilityStatus)
			: 'Unavailable';
		const productType = getProductType(this.isContentInGridCarousel(this.product), this.product.adId);

		const discountValue = getDiscountDiffValue(this.product?.price?.originalPrice, this.product?.price?.salePrice);
		const NOT_APPLICABLE = 'n/a';
		if (sku) {
			const url = `/shop/productdetails?stockcode=${sku}&name=${this.nameInKebabCase}`;
			const positionWithCarouselIndex = this.getPositionWithCarouselIndex(position);

			// GA3 productClick event
			const productDatalayerEvent: ProductDatalayerEvent = {
				id: sku,
				name: name,
				position: positionWithCarouselIndex,
				availabilityStatus,
				carouselName: this.sourceCarousel,
				price: price?.salePrice || 0,
				...(this.product?.productTag?.tagType && { specialLabel: this.product?.productTag?.tagType }),
				productType: this.productType,
			};
			const cppItemCount = this.getCppCountBasedOnTarget();
			if (typeof cppItemCount !== 'undefined') {
				productDatalayerEvent.cppItemCount = cppItemCount;
			}

			this.datalayerService.onProductClick(productDatalayerEvent, url);

			this.pushGA4DataLayer(positionWithCarouselIndex);

			// GA4 select_item event
			const itemCategories = ['item_category', 'item_category2', 'item_category3'].reduce(
				(result, fieldName, index) => ({ ...result, [fieldName]: departments?.[index]?.name }),
				{}
			);

			const ecommerce = {
				item_list_id: this.listId ?? NOT_APPLICABLE,
				item_list_name: this.listName ?? NOT_APPLICABLE,
				items: [
					{
						item_id: sku,
						item_name: name,
						discount: discountValue,
						index: positionWithCarouselIndex,
						item_brand: brand,
						...itemCategories,
						item_variant: productType,
						price: price?.salePrice,
						availability_status: availabilityStatus,
						promotion_id: extractQLCodeFromCtaName(this.content?.name) ?? this.content?.id,
						promotion_name: this.content?.name,
					},
				],
			};

			this.tealiumUtagService.link({
				...productSelectConstants,
				ecommerce,
			});
		}

		if (this.product.adId) {
			this.citrusAdService.reportClick(this.product);
		}
	}

	private isContentInGridCarousel(response: CallToActionResponse | ProductResponse): boolean {
		return (response as ProductResponse).type === 'PromotionalCarousel';
	}

	private pushGA4DataLayer(positionWithCarouselIndex: string): void {
		if (this.product.onlineSampleRealProductMapId && this.enableCartologyGA4EventTracking) {
			const { name, sku, onlineSample } = this.product;
			this.tealiumUtagService.link(
				{
					tealium_event: 'select_promotion',
					ecommerce: {
						creative_name: 'HYF Remarketing',
						creative_slot: positionWithCarouselIndex,
						promotion_id: onlineSample?.campaignId,
						promotion_name: onlineSample?.campaignName,
						items: [
							{
								item_id: sku,
								item_name: name,
								index: positionWithCarouselIndex,
								item_variant: 'Cartology_Remarketing',
							},
						],
					},
				},
				true
			);
		}
	}

	handlePurchasingUnitChange(event: ProductResponse.UnitEnum): void {
		this.productService.setPurchasingUnit(event);
	}

	getProductSource(sourceCarousel?: string): ProductSource {
		const productSource = {} as ProductSource;
		if (sourceCarousel) {
			productSource.name = sourceCarousel;
			productSource.type = 'Carousel';
		}

		if (this.listId || this.listType) {
			productSource.id = this.listId;
			productSource.name = this.listType;
			productSource.type = 'List';
		}

		return productSource;
	}

	addToTrolleyClick(
		pageSource: string,
		target: string,
		sourceCarousel: string | undefined = undefined,
		updateProductDetails = false
	): void {
		this.currentAction = ModalEvent.addToTrolley;
		const productSource = this.getProductSource(sourceCarousel);

		this.productService.addMinimumProductQuantity(
			productSource,
			pageSource,
			target,
			updateProductDetails,
			this.productType,
			this.getPositionWithCarouselIndex(this.position),
			this.getCppCountBasedOnTarget()
		);
	}

	removeFromListClick(): void {
		this.currentAction = ModalEvent.deleteFromList;
		this.modalOverlayService.open({
			eventType: ModalEvent.deleteFromList,
			templateRef: this.removeFromFavouritesModal,
		});
	}

	removeFromList(): void {
		this.productService
			.removeFromList(this.listId, this.listType)
			.pipe(
				finalize(() => {
					this.modalOverlayService.modal.close();
				}),
				takeUntil(this.destroyed$)
			)
			.subscribe(() => {
				this.visible = false;
				this.visiblityChange.emit(this.visible);

				this.cdr.markForCheck();
			});
	}

	removeItemFromTrolley(
		sourceCarousel: string | undefined,
		pageSource: string | undefined,
		target: string | undefined,
		updateProductDetails = false
	): void {
		const productSource = this.getProductSource(sourceCarousel);

		this.productService.addOrUpdateTrolley(
			0,
			productSource,
			updateProductDetails,
			false,
			this.productType,
			this.getPositionWithCarouselIndex(this.position),
			undefined,
			pageSource,
			target
		);
	}

	updateQuantity(
		quantity: any,
		pageSource: string | undefined,
		target: string | undefined,
		sourceCarousel: string | undefined = undefined,
		updateProductDetails = false
	): void {
		const productSource = this.getProductSource(sourceCarousel);
		this.currentAction = ModalEvent.addToTrolley;

		this.productService.addOrUpdateTrolley(
			quantity,
			productSource,
			updateProductDetails,
			this.isExistingSkuWhenChangingOrder,
			this.productType,
			this.getPositionWithCarouselIndex(this.position),
			this.getCppCountBasedOnTarget(),
			pageSource,
			target
		);
	}

	handleChangeAllowSubstitutes(event: { target: { checked: boolean } }): void {
		if (event.target.checked) {
			this.productService
				.allowSubstitution()
				.pipe(takeUntil(this.destroyed$))
				.subscribe(() => {
					this.allowSubstitutesProductEmitter.emit(this.sku);
				});
		} else {
			this.productService
				.disallowSubstitution()
				.pipe(takeUntil(this.destroyed$))
				.subscribe(() => {
					this.disallowSubstitutesProductEmitter.emit(this.sku);
				});
		}
	}

	shopperNoteClick(): void {
		this.currentAction = ModalEvent.addShopperNote;
		this.shopperNotesModalRef = this.modalOverlayService.open({
			eventType: ModalEvent.addShopperNote,
			templateRef: this.shopperNotesModal,
		});
		this.datalayerService.trackNotificationEvent(NotificationType.Modal, 'shopper notes', '');
	}

	closeShopperNoteModal(): void {
		this.shopperNotesModalRef?.close();
		this.datalayerService.trackNotificationEvent(NotificationType.Modal, 'shopper notes', 'X');
	}

	private updateProductService(p: ProductResponse): void {
		this.productService.setState({
			hasShopperNotes: p.hasShopperNotes,
			shopperNote: p.shopperNotes,
			product: p,
			quantityInTrolley: p?.quantity?.value || 0,
			promotionAffectedItems: undefined,
			// override the UI selectedPurchasingUnit with the value returned by the API.
			// If we dont do this, you could run into scenarios where the UI and API has different values for the same
			// and subsequent increments for the product will result in API validation errors.
			selectedPurchasingUnit: p.selectedPurchasingUnit,
		});
	}

	get alternativeUrl(): string {
		const dasFacetsUrl = !!this.product.dasFacetsUrl ? this.product.dasFacetsUrl : '';
		const joinChar = dasFacetsUrl.startsWith('/') ? '' : '/';
		return `${this.appSettingsService.getSetting('pageUrl').browse}${joinChar}${dasFacetsUrl}`;
	}

	handleFindAlternative(): void {
		this.datalayerService.trackNotificationEvent(NotificationType.Button, 'review_trolley', 'find_alternative');
		this.router.navigate([this.alternativeUrl]);
	}

	private getPositionWithCarouselIndex(position: number): string {
		return this.isGridCarousel ? `${position}.${this.carouselindex + 1}` : `${position}`;
	}

	private getCppCountBasedOnTarget(): number | undefined {
		return this.target === SearchTarget.Browse || this.target === SearchTarget.Search
			? this.cppItemCount
			: undefined;
	}
}
